import { useCallback } from 'react';
import config from '../config';
import { useSession } from './useSession';
import { makeRequest } from '../utils/fetchHelper';
import { DefaultBumpMessage, DefaultReceiveMessage } from '../common/constants';
import { getItem } from '../storage/interface';
import { Docket, DocketItemStatus, DocketStatus } from '@oolio-group/domain';
import { DOCKETS } from '../common/constants';
import { createAttemptGuard } from '../utils/docketHelper';
import { autoBumpAttemptGuard } from '../utils/docketHelper';
import { createThrottledLogger } from '../utils/throttledLog';

export interface UseOrderNotification {
  sendBumpSmsNotification: (ids: string[]) => void;
  sendReceiveSmsNotification: (ids: string[]) => void;
}

// Module-level so every caller shares one memory of already-notified ids.
export const bumpNotificationGuard = createAttemptGuard();
export const receiveNotificationGuard = createAttemptGuard();

// Keep only ids still in the CREATED poll list; drops entries for dockets that left it.
export function retainDocketGuards(currentCreatedIds: string[]): void {
  receiveNotificationGuard.retain(currentCreatedIds);
  autoBumpAttemptGuard.retain(currentCreatedIds);
}

// Recall starts a new lifecycle, so a past bump/auto-bump must not block a future one.
export function releaseDocketGuards(ids: string[]): void {
  bumpNotificationGuard.release(ids);
  autoBumpAttemptGuard.release(ids);
}

// The CREATED poll retries a failing receive-notify every ~10s, so cap how often the failure is logged.
const logReceiveNotificationError = createThrottledLogger(60_000);

export function useOrderNotification(): UseOrderNotification {
  const { session } = useSession();
  const { bumpMessage, receiveMessage, notifyOnBump, notifyOnReceive } =
    session.kitchenDisplay?.notificationConfig || {};

  const invokeBumpNotification = useCallback(
    async (id: string) => {
      const dockets =
        ((await getItem(DOCKETS + `#${DocketStatus.COMPLETED}`)) as Docket[]) ||
        [];

      const docket = dockets.find((item: Docket) => item.id == id);
      if (!docket) return;

      // No await between check and mark, so two racing callbacks can't both pass before either marks it.
      if (!bumpNotificationGuard.filterUnattempted([id]).length) return;
      bumpNotificationGuard.markAttempted([id]);

      const input = {
        ids: [id],
        smsBody: bumpMessage || DefaultBumpMessage,
        action: DocketItemStatus.BUMPED,
      };
      try {
        const response = await makeRequest(
          config.KDS_SERVICE_URI + '/docket/customer-notify',
          input,
        );
        if (!Array.isArray(response)) {
          bumpNotificationGuard.release([id]);
        }
      } catch (error) {
        bumpNotificationGuard.release([id]);
        console.error('Failed to send bump notification', id, error);
      }
    },
    [bumpMessage],
  );

  const sendBumpSmsNotification = useCallback(
    async (ids: string[]) => {
      if (!notifyOnBump) return;
      setTimeout(() => {
        invokeBumpNotification(ids[0] ?? '');
      }, 5000);
    },
    [invokeBumpNotification, notifyOnBump],
  );

  const sendReceiveSmsNotification = useCallback(
    async (ids: string[]) => {
      if (!notifyOnReceive) return;
      const unattemptedIds = receiveNotificationGuard.filterUnattempted(ids);
      if (!unattemptedIds.length) return;
      receiveNotificationGuard.markAttempted(unattemptedIds);
      const input = {
        ids: unattemptedIds,
        smsBody: receiveMessage || DefaultReceiveMessage,
        action: DocketItemStatus.CREATED,
      };
      try {
        const response = await makeRequest(
          config.KDS_SERVICE_URI + '/docket/customer-notify',
          input,
        );
        if (!Array.isArray(response)) {
          receiveNotificationGuard.release(unattemptedIds);
        }
      } catch (error) {
        receiveNotificationGuard.release(unattemptedIds);
        logReceiveNotificationError(
          'Failed to send receive notification',
          input.ids,
          error,
        );
      }
    },
    [notifyOnReceive, receiveMessage],
  );

  return {
    sendBumpSmsNotification,
    sendReceiveSmsNotification,
  };
}
